// Request payment: pick the seller's plan, pay in full or by plan, and the
// guardian who pays; the app emails that guardian a pay link
// (spec 2026-11-03-concierge-stripe-payments §3).
import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { amountSummary, authConfig, errorDetail, planOptionLabel } from '../../lib/conciergeBilling';

export default function RequestPaymentDialog({ userId, plans, guardians, onSent, onCancel }) {
  const { API_URL } = useAuth();
  const [planId, setPlanId] = useState(plans.length === 1 ? String(plans[0].id) : '');
  const [payOption, setPayOption] = useState('full');
  // Two guardians: the choice is required, so neither is pre-selected.
  const [guardianId, setGuardianId] = useState(guardians.length === 1 ? String(guardians[0].link_id) : '');
  const [months, setMonths] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const plan = plans.find(p => String(p.id) === planId) || null;
  const offersPlan = !!plan?.offers_payment_plan;
  const option = offersPlan ? payOption : 'full';
  const askMonths = !!plan && plan.length_months == null;
  const ready = !!plan && !!guardianId && !saving;

  const choosePlan = (e) => {
    setPlanId(e.target.value);
    setMonths('');
    setErr('');
  };

  const submit = (e) => {
    e.preventDefault();
    if (!ready) return;
    const body = { plan_id: plan.id, pay_option: option, guardian_link_id: Number(guardianId) };
    if (askMonths && months.trim()) body.contract_months = Number(months);
    setSaving(true);
    setErr('');
    axios.post(`${API_URL}/concierge-billing/athletes/${userId}/requests`, body, authConfig())
      .then(res => { setSaving(false); onSent(res.data); })
      .catch(ex => { setSaving(false); setErr(errorDetail(ex, 'Failed to send the payment request.')); });
  };

  return (
    <form className="cn-dialog" onSubmit={submit} data-testid="request-payment-dialog">
      <h4 className="cn-dialog__title">Request payment</h4>
      <label className="ae-field">
        <span className="ae-field__label">Plan</span>
        <select className="ae-field__input" aria-label="Plan" value={planId} onChange={choosePlan}>
          <option value="">— Choose a plan —</option>
          {plans.map(p => <option key={p.id} value={p.id}>{planOptionLabel(p)}</option>)}
        </select>
      </label>
      {plan && (
        <div className="cn-dialog__choices" role="radiogroup" aria-label="How the guardian pays">
          <label className="cn-choice">
            <input type="radio" name="cn-pay-option" aria-label="Pay in full" value="full"
                   checked={option === 'full'} onChange={() => setPayOption('full')} />
            <span>Pay in full</span>
          </label>
          {offersPlan && (
            <label className="cn-choice">
              <input type="radio" name="cn-pay-option" aria-label="Payment plan" value="plan"
                     checked={option === 'plan'} onChange={() => setPayOption('plan')} />
              <span>Payment plan</span>
            </label>
          )}
        </div>
      )}
      {askMonths && (
        <label className="ae-field">
          <span className="ae-field__label">Contract length (months)</span>
          <input className="ae-field__input" type="number" min="1" max="60" step="1"
                 aria-label="Contract length (months)" value={months}
                 onChange={e => setMonths(e.target.value)} />
          <span className="cn-field2__help">Leave blank if this plan has no contract term.</span>
        </label>
      )}
      <label className="ae-field">
        <span className="ae-field__label">Guardian who pays</span>
        <select className="ae-field__input" aria-label="Guardian who pays" value={guardianId}
                onChange={e => setGuardianId(e.target.value)}>
          {guardians.length !== 1 && <option value="">— Choose a guardian —</option>}
          {guardians.map(g => (
            <option key={g.link_id} value={g.link_id}>{g.name ? `${g.name} · ${g.email}` : g.email}</option>
          ))}
        </select>
      </label>
      {plan && <div className="cn-kv" data-testid="request-payment-amount"><span>Amount</span><span>{amountSummary(plan, option)}</span></div>}
      {err && <div className="cn-empty-line--err" role="alert">{err}</div>}
      <div className="cn-dialog__actions">
        <button type="submit" className="cn-btn cn-btn--sm" disabled={!ready}>Send request</button>
        <button type="button" className="cn-todo__action" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
